import React, { useState, useEffect } from 'react'
import { makeStyles, LinearProgress } from '@material-ui/core'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import FlexRow from '../../../../components/molecules/FlexRow'
import DenaliLoader from '../common/DenaliLoader'

const useStyles = makeStyles(() => ({
  container: {
    marginTop: 'var(--margin-sm)',
    width: '100%'
  },
  progressBar: {
    height: '6px',
    borderRadius: '3px',
    backgroundColor: 'var(--dai-color-surface4)',
    '& .MuiLinearProgress-bar': {
      backgroundColor: 'var(--dai-color-accent)'
    }
  }
}))

function MessageGenerating ({ message }) {
  const classes = useStyles()
  const [progress, setProgress] = useState(0)

  // Calculate exponentially slowing progress based on elapsed time
  useEffect(() => {
    if (!message.isGenerating || !message.createdDate) {
      return
    }

    const updateProgress = () => {
      const startTime = dayjs(message.createdDate)
      const elapsedSeconds = dayjs().diff(startTime, 'second')

      // Exponential function that slows down as it approaches 100%
      // Progress = 100 * (1 - e^(-elapsedSeconds / 20))
      // This gives fast initial progress that slows down exponentially
      const calculatedProgress = 100 * (1 - Math.exp(-elapsedSeconds / 20))

      // Cap at 95% to indicate it's not truly complete
      setProgress(Math.min(calculatedProgress, 95))
    }

    // Update immediately and then every 100ms
    updateProgress()
    const interval = setInterval(updateProgress, 100)

    return () => clearInterval(interval)
  }, [message.isGenerating, message.createdDate])

  if (!message.isGenerating) {
    return null
  }

  return (
    <div className={classes.container}>
      <LinearProgress
        variant='determinate'
        value={progress}
        className={classes.progressBar}
      />
      {message.generationStatus && (
        <FlexRow className='justify-center items-center font-sans font-size-md font-serif p-xl gap-xl'>
          <span><DenaliLoader size='52px' /></span>
          <span style={{ color: 'var(--dai-color-text-low)' }}>{message.generationStatus}</span>
        </FlexRow>
      )}
    </div>
  )
}

MessageGenerating.propTypes = {
  message: PropTypes.shape({
    threadMessageId: PropTypes.string,
    actor: PropTypes.oneOf(['user', 'agent']),
    message: PropTypes.string,
    attachments: PropTypes.array,
    isGenerating: PropTypes.bool,
    generationStatus: PropTypes.string,
    createdDate: PropTypes.string,
    data: PropTypes.shape({
      events: PropTypes.array,
      lastEventSequence: PropTypes.number,
      lastUpdateTimestamp: PropTypes.string,
      currentStepId: PropTypes.string
    })
  }).isRequired
}

export default MessageGenerating
